import { formatarDia, Periodo, somarDias } from './Dashboards.periodo'
import type { DiaCanal, ErroAgrupado } from './Dashboards.scopes'

// Cores da paleta de referência validada para daltonismo (slots 1 e 2) e tinta neutra para eixos e grade.
// A cor segue a série: "Erro 400" é sempre azul e "Outras falhas" sempre laranja, mesmo que uma delas suma.
export const COR = {
    serie1: '#2a78d6',
    serie2: '#eb6834',
    texto: '#52514e',
    eixo: '#c3c2b7',
    grade: '#ecebe7'
}

export const ROTULO_CANAL: Record<string, string> = {
    whatsapp: 'WhatsApp',
    hubspot: 'HubSpot',
    'deal-phase-change': 'Mudança de fase',
    'deal-notification': 'Avisos de negócio',
    'deal-pendencia': 'Pendências',
    'chat-save': 'Gravação do chat',
    'news-notification': 'Campanhas'
}

export const rotuloCanal = (canal: string) => ROTULO_CANAL[canal] ?? canal

// Códigos de erro da Meta mais comuns na LIA (https://developers.facebook.com/docs/whatsapp/cloud-api/support/error-codes).
const ERRO_META: Record<string, string> = {
    '2': 'Serviço da Meta indisponível no momento',
    '131000': 'Erro interno da Meta',
    '131026': 'Número não pode receber a mensagem',
    '131047': 'Fora da janela de 24 h: só template',
    '131049': 'Meta segurou a mensagem para não saturar o destinatário',
    '131056': 'Muitas mensagens ao mesmo número em pouco tempo',
    '130429': 'Limite de envio da conta atingido',
    '132000': 'Variáveis do template não batem',
    '132001': 'Template não existe ou não está aprovado'
}

/** Uma linha legível para o erro agrupado. */
export function descreverErro(erro: ErroAgrupado): string {
    if (erro.codigoMeta && ERRO_META[erro.codigoMeta]) return ERRO_META[erro.codigoMeta]
    if (erro.http == null) {
        return /fetch failed|timeout|aborted/i.test(erro.exemplo)
            ? 'Sem resposta da API (rede ou tempo esgotado)'
            : 'Sem código HTTP'
    }
    return erro.exemplo.replace(/^WhatsApp API responded with status \d+: /, '').slice(0, 140)
}

/** O texto de erro de uma mensagem (last_error) no mesmo formato da tabela de erros: "400 · 131056 · descrição". */
export function resumirErro(texto: string): string {
    if (!texto) return '—'
    const http = texto.match(/status (\d{3})/)?.[1]
    const codigoMeta = texto.match(/\(#(\d+)\)/)?.[1] ?? null
    const descricao = descreverErro({ http: http ? Number(http) : null, codigoMeta, qtde: 1, exemplo: texto })
    return [http, codigoMeta, descricao].filter(Boolean).join(' · ')
}

/** Todos os dias do período, para o eixo não pular os dias sem movimento. */
export function diasDoPeriodo(periodo: Periodo): string[] {
    const dias: string[] = []
    for (let dia = periodo.de; dia <= periodo.ate; dia = somarDias(dia, 1)) {
        dias.push(dia)
    }
    return dias
}

/** Soma um campo da série por dia, só nos canais pedidos (todos, sem filtro). */
export function somaPorDia(
    serie: DiaCanal[],
    dias: string[],
    campo: (d: DiaCanal) => number,
    canais?: string[]
): number[] {
    const porDia = new Map<string, number>()
    for (const d of serie) {
        if (canais && !canais.includes(d.canal)) continue
        porDia.set(d.dia, (porDia.get(d.dia) ?? 0) + campo(d))
    }
    return dias.map((dia) => porDia.get(dia) ?? 0)
}

const numero = (n: number) => n.toLocaleString('pt-BR')

function eixoDias(dias: string[]) {
    return {
        type: 'category',
        data: dias.map((d) => formatarDia(d)),
        axisLine: { lineStyle: { color: COR.eixo } },
        axisTick: { show: false },
        axisLabel: { color: COR.texto, hideOverlap: true }
    }
}

function eixoValor(formatador?: (v: number) => string) {
    return {
        type: 'value',
        splitLine: { lineStyle: { color: COR.grade } },
        axisLabel: { color: COR.texto, formatter: formatador ?? ((v: number) => numero(v)) }
    }
}

const GRADE = { left: 8, right: 16, top: 16, bottom: 8, containLabel: true }
const GRADE_COM_LEGENDA = { ...GRADE, top: 40 }
const LEGENDA = { top: 0, left: 0, icon: 'roundRect', itemWidth: 12, itemHeight: 12, textStyle: { color: COR.texto } }
const BARRA = { borderRadius: [4, 4, 0, 0] }

/** Mensagens do WhatsApp enviadas por dia. Uma série: o título do card a nomeia. */
export function opcaoEntregas(serie: DiaCanal[], periodo: Periodo) {
    const dias = diasDoPeriodo(periodo)
    return {
        grid: GRADE,
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, valueFormatter: (v: number) => numero(v) },
        xAxis: eixoDias(dias),
        yAxis: eixoValor(),
        series: [
            {
                name: 'Enviadas',
                type: 'bar',
                barMaxWidth: 28,
                itemStyle: { ...BARRA, color: COR.serie1 },
                data: somaPorDia(serie, dias, (d) => d.enviadas, ['whatsapp'])
            }
        ]
    }
}

/** Tentativas com erro por dia, em todos os canais: as respostas 400 separadas das outras. */
export function opcaoFalhas(serie: DiaCanal[], periodo: Periodo) {
    const dias = diasDoPeriodo(periodo)
    const erros400 = somaPorDia(serie, dias, (d) => d.erros400)
    const outras = somaPorDia(serie, dias, (d) => d.falhas - d.erros400)
    return {
        grid: GRADE_COM_LEGENDA,
        legend: LEGENDA,
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, valueFormatter: (v: number) => numero(v) },
        xAxis: eixoDias(dias),
        yAxis: eixoValor(),
        series: [
            {
                name: 'Erro 400',
                type: 'bar',
                stack: 'falhas',
                barMaxWidth: 28,
                itemStyle: { color: COR.serie1, borderColor: '#fff', borderWidth: 1 },
                data: erros400
            },
            {
                name: 'Outras falhas',
                type: 'bar',
                stack: 'falhas',
                barMaxWidth: 28,
                itemStyle: { ...BARRA, color: COR.serie2, borderColor: '#fff', borderWidth: 1 },
                data: outras
            }
        ]
    }
}

const segundos = (ms: number | null) => (ms == null ? null : Math.round(ms / 100) / 10)

/** Tempo entre entrar na fila e ser enviada, no WhatsApp: mediana e o pior 5%. */
export function opcaoEspera(serie: DiaCanal[], periodo: Periodo) {
    const dias = diasDoPeriodo(periodo)
    const doWhatsapp = new Map(serie.filter((d) => d.canal === 'whatsapp').map((d) => [d.dia, d]))
    const linha = (name: string, cor: string, campo: (d: DiaCanal) => number | null) => ({
        name,
        type: 'line',
        connectNulls: false,
        showSymbol: false,
        symbolSize: 8,
        lineStyle: { width: 2, color: cor },
        itemStyle: { color: cor, borderColor: '#fff', borderWidth: 2 },
        data: dias.map((dia) => {
            const d = doWhatsapp.get(dia)
            return d ? segundos(campo(d)) : null
        })
    })
    return {
        grid: GRADE_COM_LEGENDA,
        legend: LEGENDA,
        tooltip: {
            trigger: 'axis',
            valueFormatter: (v: number | null) => (v == null ? '—' : `${v.toLocaleString('pt-BR')} s`)
        },
        xAxis: eixoDias(dias),
        yAxis: eixoValor((v) => `${v.toLocaleString('pt-BR')} s`),
        series: [
            linha('Mediana', COR.serie1, (d) => d.esperaP50Ms),
            linha('95% das mensagens', COR.serie2, (d) => d.esperaP95Ms)
        ]
    }
}

/** 95 → "1 min 35 s"; usado na idade da mensagem mais antiga ainda na fila. */
export function formatarDuracao(segundosTotais: number | null): string {
    if (segundosTotais == null) return '—'
    if (segundosTotais < 60) return `${segundosTotais} s`
    const minutos = Math.floor(segundosTotais / 60)
    if (minutos < 60) return `${minutos} min ${segundosTotais % 60} s`
    const horas = Math.floor(minutos / 60)
    if (horas < 48) return `${horas} h ${minutos % 60} min`
    return `${Math.floor(horas / 24)} dias`
}
